import { Component, OnInit, Input, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  BdcContentV2Service,
  BdcPropertyDetailsResponse,
  BdcUnitMetadataUnitNameItem,
  BdcUnitMetadataUnitTypeItem,
  BdcUnitResponse,
} from '@tokeet-frontend/channel-content'
import {
  ActionFailed,
  ConfirmDialogService,
  Toaster,
  filterPredicateFactory,
  isEmptyTable,
  localeCompareSort,
} from '@tokeet-frontend/tv3-platform'
import { finalize, switchMap } from 'rxjs/operators'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { BdcPropertyUnitDialogService } from '../../bdc-property-unit-dialog/bdc-property-unit-dialog.service'
import { combineLatest } from 'rxjs'

@Component({
  selector: 'app-bdc-property-units',
  templateUrl: './bdc-property-units.component.html',
  styleUrls: ['./bdc-property-units.component.scss'],
})
export class BdcPropertyUnitsComponent implements OnInit {
  @Input() propertyId: number

  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  dataSource = new MatTableDataSource<BdcUnitResponse>([])
  set units(items: BdcUnitResponse[]) {
    this.dataSource.data = items
  }
  isEmptyTable$ = isEmptyTable(this.dataSource)

  get units() {
    return this.dataSource.data
  }

  propertyDetails: BdcPropertyDetailsResponse

  displayedColumns = ['unit_name_id', 'unit_id', 'unit_type_id', 'edit']
  unitNamesById: Record<number, BdcUnitMetadataUnitNameItem> = {}
  unitTypesById: Record<number, BdcUnitMetadataUnitTypeItem> = {}

  isLoading = true
  constructor(
    private store: Store<any>,
    private toaster: Toaster,
    private confirm: ConfirmDialogService,
    private contentService: BdcContentV2Service,
    private unitDialog: BdcPropertyUnitDialogService
  ) {}
  ngOnInit(): void {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort
    this.dataSource.filterPredicate = filterPredicateFactory(this.displayedColumns, (element) => [
      element.unit_id.toString(),
      this.unitNamesById[element.unit_name_id]?.unit_name,
      this.unitTypesById[element.configuration.unit_type_id]?.unit_type_name,
    ])

    this.onRefresh()
  }

  onRefresh() {
    combineLatest([
      this.contentService.getPropertyUnits(this.propertyId),
      this.contentService.getUnitMetadata(),
      this.contentService.getProperty(this.propertyId),
    ])
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        ([units, metadata, property]) => {
          this.propertyDetails = property
          this.units = units
          this.unitNamesById = metadata.unit_names.reduce((acc, item) => {
            acc[item.unit_name_id] = item
            return acc
          }, {})
          this.unitTypesById = metadata.unit_types.reduce((acc, item) => {
            acc[item.unit_type_id] = item
            return acc
          }, {})
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onSearch(term: string) {
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  onRemove(item: BdcUnitResponse) {
    this.confirm
      .confirm({
        title: 'Delete Unit',
        body: `Are you sure you want to delete this unit?`,
      })
      .pipe(switchMap(() => this.contentService.deletePropertyUnit(this.propertyId, item.unit_id)))
      .subscribe(
        () => {
          this.toaster.success($localize`Unit deleted successfully`)
          this.units = this.units.filter((t) => t.unit_id !== item.unit_id)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onAdd() {
    this.unitDialog
      .open(this.propertyId, this.propertyDetails.booking_property_category)
      .afterClosed()
      .subscribe(() => {
        this.onRefresh()
      })
  }

  onEdit(item: BdcUnitResponse) {
    this.unitDialog
      .open(this.propertyId, this.propertyDetails.booking_property_category, item)
      .afterClosed()
      .subscribe(() => {
        this.onRefresh()
      })
  }
}
